import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/lib/api-client';
import type {
  HoldRequestComment,
  LineItemComment,
  CreateHoldRequestCommentData,
  CreateLineItemCommentData,
  UpdateCommentData,
  HoldRequestCommentSearchResult,
  LineItemCommentSearchResult,
  CommentPaginationOptions,
  CommentSearchFilters,
} from '@/types';

/**
 * Hook to fetch hold request comments with pagination
 */
export function useHoldRequestComments(
  holdRequestId: string,
  options: CommentPaginationOptions = {}
) {
  const { limit = 20, offset = 0, sortOrder = 'asc' } = options;

  return useQuery({
    queryKey: ['holdRequestComments', holdRequestId, limit, offset, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sortOrder,
      });
      const response = await get<HoldRequestCommentSearchResult>(
        `/holds/${holdRequestId}/comments?${params.toString()}`
      );
      return response;
    },
    enabled: !!holdRequestId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch line item comments with pagination
 */
export function useLineItemComments(
  lineItemId: string,
  options: CommentPaginationOptions = {}
) {
  const { limit = 20, offset = 0, sortOrder = 'asc' } = options;

  return useQuery({
    queryKey: ['lineItemComments', lineItemId, limit, offset, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sortOrder,
      });
      const response = await get<LineItemCommentSearchResult>(
        `/items/${lineItemId}/comments?${params.toString()}`
      );
      return response;
    },
    enabled: !!lineItemId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to create a hold request comment
 */
export function useCreateHoldRequestComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateHoldRequestCommentData) => {
      const response = await post<HoldRequestComment>(
        `/holds/${data.holdRequestId}/comments`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate all pages of comments for this hold request
      queryClient.invalidateQueries({
        queryKey: ['holdRequestComments', variables.holdRequestId],
      });
    },
  });
}

/**
 * Hook to create a line item comment
 */
export function useCreateLineItemComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLineItemCommentData) => {
      const response = await post<LineItemComment>(
        `/items/${data.lineItemId}/comments`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate all pages of comments for this line item
      queryClient.invalidateQueries({
        queryKey: ['lineItemComments', variables.lineItemId],
      });
    },
  });
}

/**
 * Hook to update a comment
 */
export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      data,
    }: {
      commentId: string;
      data: UpdateCommentData;
    }) => {
      const response = await put<HoldRequestComment | LineItemComment>(
        `/comments/${commentId}`,
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate all comment queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['holdRequestComments'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to delete a comment
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      await del(`/comments/${commentId}`);
      return commentId;
    },
    onSuccess: () => {
      // Invalidate all comment queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['holdRequestComments'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to search hold request comments
 */
export function useSearchHoldRequestComments(
  holdRequestId: string,
  filters: CommentSearchFilters,
  options: CommentPaginationOptions = {}
) {
  const { limit = 20, offset = 0, sortOrder = 'asc' } = options;

  return useQuery({
    queryKey: [
      'searchHoldRequestComments',
      holdRequestId,
      filters,
      limit,
      offset,
      sortOrder,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sortOrder,
      });

      // Add filters to params
      if (filters.searchText) params.append('searchText', filters.searchText);
      if (filters.authorId) params.append('authorId', filters.authorId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.actionContext)
        params.append('actionContext', filters.actionContext);
      if (filters.includeSystemGenerated !== undefined)
        params.append(
          'includeSystemGenerated',
          filters.includeSystemGenerated.toString()
        );
      if (filters.includeDeleted !== undefined)
        params.append('includeDeleted', filters.includeDeleted.toString());

      const response = await get<HoldRequestCommentSearchResult>(
        `/holds/${holdRequestId}/comments/search?${params.toString()}`
      );
      return response;
    },
    enabled: !!holdRequestId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to search line item comments
 */
export function useSearchLineItemComments(
  lineItemId: string,
  filters: CommentSearchFilters,
  options: CommentPaginationOptions = {}
) {
  const { limit = 20, offset = 0, sortOrder = 'asc' } = options;

  return useQuery({
    queryKey: [
      'searchLineItemComments',
      lineItemId,
      filters,
      limit,
      offset,
      sortOrder,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sortOrder,
      });

      // Add filters to params
      if (filters.searchText) params.append('searchText', filters.searchText);
      if (filters.authorId) params.append('authorId', filters.authorId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.actionContext)
        params.append('actionContext', filters.actionContext);
      if (filters.includeSystemGenerated !== undefined)
        params.append(
          'includeSystemGenerated',
          filters.includeSystemGenerated.toString()
        );
      if (filters.includeDeleted !== undefined)
        params.append('includeDeleted', filters.includeDeleted.toString());

      const response = await get<LineItemCommentSearchResult>(
        `/items/${lineItemId}/comments/search?${params.toString()}`
      );
      return response;
    },
    enabled: !!lineItemId,
    staleTime: 30000, // 30 seconds
  });
}
